/* eslint-disable camelcase */
import { FC, useRef } from 'react'
import {
  BooleanInput,
  CreateBase,
  EditBase,
  SimpleForm,
  TextInput,
  useGetList,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin'
import { Alert, Box, CircularProgress, Divider, Typography } from '@mui/material'
import {
  OrgOpenWebuiConfigCreate,
  OrgOpenWebuiConfigOut,
  OrgOpenWebuiConfigRevealOut,
  OrganizationOut,
} from '../../types/apiRequests'
import { SaveOnlyToolbar, SecretRevealInput } from '../../components'
import { getJson, getUrl } from '../../providers/data'

const SectionHeader: FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <Box sx={{ width: '100%', mt: 1 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{subtitle}</Typography>
  </Box>
)

const ConfigForm: FC<{
  configId?: string
  defaultValues?: Partial<OrgOpenWebuiConfigCreate>
  canEnableChat: boolean
}> = ({
  configId, defaultValues, canEnableChat,
}) => {
  const translate = useTranslate()
  const revealCache = useRef<Promise<OrgOpenWebuiConfigRevealOut> | null>(null)

  const fetchReveal = (): Promise<OrgOpenWebuiConfigRevealOut> => {
    if (!configId) return Promise.resolve({})
    if (!revealCache.current) {
      revealCache.current = getJson<OrgOpenWebuiConfigRevealOut>(
        `${getUrl()}/org_openwebui_config/${configId}/reveal`,
      )
    }
    return revealCache.current
  }

  return (
    <SimpleForm defaultValues={defaultValues} toolbar={<SaveOnlyToolbar />}>
      <SectionHeader
        title={translate('openwebuiConfig.sectionLitellm')}
        subtitle={translate('openwebuiConfig.sectionLitellmHelp')}
      />
      <SecretRevealInput
        source="litellm_api_key"
        label="openwebuiConfig.litellmApiKey"
        helperText="openwebuiConfig.litellmApiKeyHelp"
        onReveal={async () => (await fetchReveal()).litellm_api_key ?? null}
      />
      <TextInput
        source="litellm_key_name"
        label="openwebuiConfig.keyName"
        helperText="openwebuiConfig.keyNameHelp"
        fullWidth
      />
      <TextInput
        source="litellm_key_id"
        label="openwebuiConfig.keyId"
        helperText="openwebuiConfig.keyIdHelp"
        fullWidth
      />

      <Divider sx={{ width: '100%', my: 2 }} />

      <SectionHeader
        title={translate('openwebuiConfig.sectionOpenwebui')}
        subtitle={translate('openwebuiConfig.sectionOpenwebuiHelp')}
      />
      <TextInput
        source="openwebui_url"
        label="openwebuiConfig.openwebuiUrl"
        helperText="openwebuiConfig.openwebuiUrlHelp"
        fullWidth
      />
      <SecretRevealInput
        source="openwebui_api_key"
        label="openwebuiConfig.openwebuiApiKey"
        helperText="openwebuiConfig.openwebuiApiKeyHelp"
        onReveal={async () => (await fetchReveal()).openwebui_api_key ?? null}
      />

      <Divider sx={{ width: '100%', my: 2 }} />

      <BooleanInput
        source="enabled"
        label="openwebuiConfig.enabled"
        disabled={!canEnableChat}
        helperText={canEnableChat ? false : translate('openwebuiConfig.enabledTierHelp')}
      />
    </SimpleForm>
  )
}

export const OrgOpenWebuiConfigTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const refresh = useRefresh()
  const translate = useTranslate()

  const { data, isLoading } = useGetList<OrgOpenWebuiConfigOut>(
    'org_openwebui_config',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created', order: 'DESC' },
      filter: { organization_id: record?.id },
    },
    { enabled: !!record?.id },
  )

  if (isLoading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
  if (!record?.id) return null

  const existing = data?.[0]
  // Chat may only be activated for production (enterprise) tier organizations.
  // A new organization always starts with chat disabled.
  const canEnableChat = record.hub_tier === 'enterprise'
  const newRecord: OrgOpenWebuiConfigCreate = { organization_id: record.id, enabled: false }

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        {existing
          ? translate('openwebuiConfig.descriptionExisting')
          : translate('openwebuiConfig.descriptionNew')}
      </Typography>

      {!canEnableChat && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('openwebuiConfig.tierWarning')}
        </Alert>
      )}

      {existing ? (
        <EditBase resource="org_openwebui_config" id={existing.id} mutationMode="pessimistic">
          <ConfigForm configId={existing.id} canEnableChat={canEnableChat} />
        </EditBase>
      ) : (
        <CreateBase
          resource="org_openwebui_config"
          record={newRecord}
          mutationOptions={{ onSuccess: () => refresh() }}
        >
          <ConfigForm defaultValues={newRecord} canEnableChat={canEnableChat} />
        </CreateBase>
      )}
    </Box>
  )
}

export default OrgOpenWebuiConfigTab
